import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useRef, 
  ReactNode, 
  useEffect,
  useMemo
} from 'react';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';

type CalendarVariant = 'full' | 'insights' | 'timetracking';
type EmployeeFilter = string[] | string | null; // Array of employee IDs, single ID, 'all', 'unassigned', or null
type StatusFilter = string[] | string | null; // Array of status values, single status, 'all', or null
type FocusDirection = 'up' | 'down' | 'left' | 'right' | 'next' | 'previous';
type PointerType = 'mouse' | 'touch' | 'pen';

// Accessibility and interaction interfaces
interface AccessibilityState {
  screenReaderEnabled: boolean;
  highContrastMode: boolean;
  reducedMotion: boolean;
  colorBlindFriendly: boolean;
}

interface KeyboardState {
  lastKeyPressed: string | null;
  isNavigating: boolean;
  focusedShiftId: string | null;
  keyboardUser: boolean;
}

interface PointerState {
  pointerType: PointerType;
  isHovering: boolean;
  lastPointerPosition: { x: number; y: number } | null;
  touchSupported: boolean;
}

interface DimmingAriaAttributes {
  'aria-hidden': 'true' | 'false';
  'aria-label': string;
  'aria-describedby': string;
  'role': string;
  'tabindex': '-1' | '0';
}

interface DimmingEventHandlers {
  onMouseEnter: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onMouseLeave: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onFocus: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onBlur: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onClick: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onKeyDown: (event: KeyboardEvent, shiftId: string, calendarVariant?: CalendarVariant) => void;
  onTouchStart: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onTouchEnd: (shiftId: string, calendarVariant?: CalendarVariant) => void;
  onPointerEnter: (shiftId: string, pointerType: PointerType, calendarVariant?: CalendarVariant) => void;
  onPointerLeave: (shiftId: string, pointerType: PointerType, calendarVariant?: CalendarVariant) => void;
}

interface DimmingContextType {
  // Core state
  selectedEmployeeFilter: EmployeeFilter;
  selectedStatusFilter: StatusFilter;
  hoveredShiftId: string | null;
  shiftWithOpenSelect: string | null;
  activeTooltipShiftId: string | null;
  draggingShiftId: string | null;
  
  // Accessibility and interaction state
  accessibilityState: AccessibilityState;
  keyboardState: KeyboardState;
  pointerState: PointerState;
  
  // State setters
  setSelectedEmployeeFilter: (filter: EmployeeFilter) => void;
  setSelectedStatusFilter: (filter: StatusFilter) => void;
  setHoveredShiftId: (shiftId: string | null, calendarVariant?: CalendarVariant) => void;
  setShiftWithOpenSelect: (shiftId: string | null) => void;
  setActiveTooltipShiftId: (shiftId: string | null, preferredSide?: 'top' | 'bottom' | 'left' | 'right') => void;
  setDraggingShiftId: (shiftId: string | null) => void;
  
  // Event handlers
  eventHandlers: DimmingEventHandlers;
  
  // Helper functions
  getDimmingClass: (shiftId: string, employeeId?: string | null, calendarVariant?: CalendarVariant, shiftStatus?: ShiftStatus | string) => string;
  getDimmingAriaAttributes: (shiftId: string, employeeId?: string | null, calendarVariant?: CalendarVariant, shiftStatus?: ShiftStatus | string) => DimmingAriaAttributes;
  getListItemDimmingClass: (shiftId: string, employeeId?: string | null) => string;
  shouldDimForEmployeeFilter: (filter: EmployeeFilter, employeeId?: string | null) => boolean;
  shouldDimForStatusFilter: (filter: StatusFilter, shiftStatus?: ShiftStatus | string) => boolean;
  
  // Tooltip coordination functions
  shouldShowTooltip: (shiftId: string) => boolean;
  getTooltipSide: (shiftId: string, defaultSide?: 'top' | 'bottom' | 'left' | 'right') => 'top' | 'bottom' | 'left' | 'right';
  
  // Navigation functions
  navigateShifts: (direction: FocusDirection, currentShiftId?: string) => string | null;
  focusShift: (shiftId: string) => void;
  
  // Accessibility functions
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  updateAccessibilityState: (updates: Partial<AccessibilityState>) => void;
  
  // Clear functions
  clearAllDimming: () => void;
  clearKeyboardNavigation: () => void;
}

const DimmingContext = createContext<DimmingContextType | undefined>(undefined);

interface DimmingProviderProps {
  children: ReactNode;
}

export const DimmingProvider: React.FC<DimmingProviderProps> = ({ children }) => {
  // Core state
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState<EmployeeFilter>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilter>(null);
  const [hoveredShiftId, setHoveredShiftIdState] = useState<string | null>(null);
  const [shiftWithOpenSelect, setShiftWithOpenSelect] = useState<string | null>(null);
  const [activeTooltipShiftId, setActiveTooltipShiftIdState] = useState<string | null>(null);
  const [tooltipPreferredSide, setTooltipPreferredSide] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const [draggingShiftId, setDraggingShiftId] = useState<string | null>(null);
  
  // Accessibility and interaction state
  const [accessibilityState, setAccessibilityState] = useState<AccessibilityState>({
    screenReaderEnabled: false,
    highContrastMode: false,
    reducedMotion: false,
    colorBlindFriendly: false
  });
  
  const [keyboardState, setKeyboardState] = useState<KeyboardState>({
    lastKeyPressed: null,
    isNavigating: false,
    focusedShiftId: null,
    keyboardUser: false
  });
  
  const [pointerState, setPointerState] = useState<PointerState>({
    pointerType: 'mouse',
    isHovering: false,
    lastPointerPosition: null,
    touchSupported: false
  });
  
  // Refs for managing interactions
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const keyboardTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const announcementRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const shiftElementsRef = useRef<Map<string, HTMLElement>>(new Map());

  // Initialize accessibility detection on mount
  useEffect(() => {
    const detectAccessibilityFeatures = () => {
      // Detect screen reader
      const screenReaderEnabled = !!navigator.userAgent.match(/JAWS|NVDA|ORCA|VoiceOver|TalkBack/);
      
      // Detect reduced motion preference
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      // Detect high contrast mode (Windows)
      const highContrastMode = window.matchMedia('(prefers-contrast: high)').matches;
      
      // Detect touch support
      const touchSupported = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      setAccessibilityState(prev => ({
        ...prev,
        screenReaderEnabled,
        reducedMotion,
        highContrastMode
      }));
      
      setPointerState(prev => ({
        ...prev,
        touchSupported
      }));
    };
    
    detectAccessibilityFeatures();
    
    // Listen for accessibility preference changes
    const reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    const highContrastMQ = window.matchMedia('(prefers-contrast: high)');
    
    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      setAccessibilityState(prev => ({ ...prev, reducedMotion: e.matches }));
    };
    
    const handleHighContrastChange = (e: MediaQueryListEvent) => {
      setAccessibilityState(prev => ({ ...prev, highContrastMode: e.matches }));
    };
    
    reducedMotionMQ.addEventListener('change', handleReducedMotionChange);
    highContrastMQ.addEventListener('change', handleHighContrastChange);
    
    return () => {
      reducedMotionMQ.removeEventListener('change', handleReducedMotionChange);
      highContrastMQ.removeEventListener('change', handleHighContrastChange);
    };
  }, []);
  
  // Create screen reader announcement element
  useEffect(() => {
    if (!announcementRef.current) {
      const announceElement = document.createElement('div');
      announceElement.setAttribute('aria-live', 'polite');
      announceElement.setAttribute('aria-atomic', 'true');
      announceElement.className = 'sr-only';
      announceElement.style.position = 'absolute';
      announceElement.style.left = '-10000px';
      announceElement.style.width = '1px';
      announceElement.style.height = '1px';
      announceElement.style.overflow = 'hidden';
      document.body.appendChild(announceElement);
      announcementRef.current = announceElement;
    }
    
    return () => {
      if (announcementRef.current) {
        document.body.removeChild(announcementRef.current);
        announcementRef.current = null;
      }
    };
  }, []);
  
  // Screen reader announcement function
  const announceToScreenReader = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announcementRef.current) {
      announcementRef.current.setAttribute('aria-live', priority);
      announcementRef.current.textContent = message;
      
      // Clear after announcement to allow repeat announcements
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = '';
        }
      }, 1000);
    }
  }, []);
  
  // Update accessibility state function
  const updateAccessibilityState = useCallback((updates: Partial<AccessibilityState>) => {
    setAccessibilityState(prev => ({ ...prev, ...updates }));
  }, []);
  
  // **HOVER FIX**: Improved keyboard user detection with better mouse interaction handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key.startsWith('Arrow')) {
        setKeyboardState(prev => ({ 
          ...prev, 
          keyboardUser: true,
          lastKeyPressed: e.key,
          isNavigating: true
        }));
        
        // **HOVER FIX**: Reduce timeout for keyboard navigation state (was 3000ms, now 1000ms)
        if (keyboardTimeoutRef.current) {
          clearTimeout(keyboardTimeoutRef.current);
        }
        keyboardTimeoutRef.current = setTimeout(() => {
          setKeyboardState(prev => ({ ...prev, isNavigating: false }));
        }, 1000); // Reduced from 3000ms to 1000ms for better hover responsiveness
      }
    };
    
    // **HOVER FIX**: Enhanced mouse move detection that properly resets keyboard user flag
    const handleMouseMove = (e: MouseEvent) => {
      // Only reset keyboard user if there's actual movement (not just synthetic events)
      if (e.isTrusted && (e.movementX !== 0 || e.movementY !== 0)) {
        setKeyboardState(prev => ({ 
          ...prev, 
          keyboardUser: false,
          isNavigating: false // Also clear navigation state on mouse movement
        }));
        
        // Clear any pending keyboard timeout
        if (keyboardTimeoutRef.current) {
          clearTimeout(keyboardTimeoutRef.current);
          keyboardTimeoutRef.current = null;
        }
      }
    };
    
    // **HOVER FIX**: Add mouse enter/click handlers for immediate keyboard state reset
    const handleMouseInteraction = () => {
      setKeyboardState(prev => ({ 
        ...prev, 
        keyboardUser: false,
        isNavigating: false
      }));
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseenter', handleMouseInteraction, true); // Use capture phase
    document.addEventListener('click', handleMouseInteraction);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseInteraction, true);
      document.removeEventListener('click', handleMouseInteraction);
      if (keyboardTimeoutRef.current) {
        clearTimeout(keyboardTimeoutRef.current);
      }
    };
  }, []);
  
  // **HOVER FIX**: Enhanced hover setter with improved responsiveness and proper cleanup
  const setHoveredShiftId = useCallback((shiftId: string | null, calendarVariant: CalendarVariant = 'full') => {
    // Clear any existing timeout FIRST - always do this regardless of other conditions
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // ALWAYS allow clearing hover state (when shiftId is null)
    // This prevents stuck hover states
    if (shiftId === null) {
      setHoveredShiftIdState(null);
      return;
    }

    // Only block SETTING new hover for insights variant or when select is open
    if (calendarVariant === 'insights' || shiftWithOpenSelect) {
      return;
    }

    // Set new hover with delay
    const delay = accessibilityState.reducedMotion ? 0 : 1000;

    if (delay === 0) {
      setHoveredShiftIdState(shiftId);
    } else {
      hoverTimeoutRef.current = setTimeout(() => {
        // Double-check conditions haven't changed during timeout
        if (!shiftWithOpenSelect) {
          setHoveredShiftIdState(shiftId);
        }
        hoverTimeoutRef.current = null;
      }, delay);
    }

    // Announce to screen reader
    if (accessibilityState.screenReaderEnabled) {
      announceToScreenReader(`Shift ${shiftId} highlighted`);
    }
  }, [accessibilityState.reducedMotion, accessibilityState.screenReaderEnabled, announceToScreenReader, shiftWithOpenSelect]);

  // Helper function to check if a shift should be dimmed based on employee filter
  const shouldDimForEmployeeFilter = useCallback((filter: EmployeeFilter, employeeId?: string | null): boolean => {
    if (!filter) return false;

    // Handle array-based filters (multiple selections)
    if (Array.isArray(filter)) {
      if (filter.length === 0) return false; // No filter applied

      // For multiple selections, highlight shifts that match ANY of the selected employees (OR logic)
      // This means we should dim shifts that don't match ANY of the selected employees
      const shouldHighlight = filter.some(employeeFilter => {
        if (employeeFilter === 'all') {
          return true; // 'all' matches everything
        } else {
          return employeeId === employeeFilter; // Matches specific employee
        }
      });

      return !shouldHighlight; // Dim if should NOT highlight
    }

    // Handle single string filters (backward compatibility)
    switch (filter) {
      case 'all':
        return false; // Show all shifts normally
      default:
        // Specific employee filter - dim shifts not assigned to this employee
        return employeeId !== filter;
    }
  }, []);

  // Helper function to check if a shift should be dimmed based on status filter
  const shouldDimForStatusFilter = useCallback((filter: StatusFilter, shiftStatus?: ShiftStatus | string): boolean => {
    if (!filter) return false;

    // Handle array-based filters (multiple selections)
    if (Array.isArray(filter)) {
      if (filter.length === 0) return false; // No filter applied

      // For multiple selections, highlight shifts that match ANY of the selected statuses (OR logic)
      // This means we should dim shifts that don't match ANY of the selected statuses
      const shouldHighlight = filter.some(statusFilter => {
        if (statusFilter === 'all') {
          return true; // 'all' matches everything
        } else {
          return shiftStatus === statusFilter; // Matches specific status
        }
      });

      return !shouldHighlight; // Dim if should NOT highlight
    }

    // Handle single string filters (backward compatibility)
    switch (filter) {
      case 'all':
        return false; // Show all shifts normally
      default:
        // Specific status filter - dim shifts that don't match this status
        return shiftStatus !== filter;
    }
  }, []);
  
  // Navigation function for keyboard users
  const navigateShifts = useCallback((direction: FocusDirection, currentShiftId?: string): string | null => {
    const allShiftIds = Array.from(shiftElementsRef.current.keys());
    if (allShiftIds.length === 0) return null;
    
    if (!currentShiftId) {
      // Return first shift if none currently focused
      return allShiftIds[0] || null;
    }
    
    const currentIndex = allShiftIds.indexOf(currentShiftId);
    if (currentIndex === -1) return null;
    
    let nextIndex: number;
    
    switch (direction) {
      case 'next':
      case 'down':
      case 'right':
        nextIndex = (currentIndex + 1) % allShiftIds.length;
        break;
      case 'previous':
      case 'up':
      case 'left':
        nextIndex = currentIndex === 0 ? allShiftIds.length - 1 : currentIndex - 1;
        break;
      default:
        return null;
    }
    
    return allShiftIds[nextIndex] || null;
  }, []);
  
  // Focus management function
  const focusShift = useCallback((shiftId: string) => {
    const element = shiftElementsRef.current.get(shiftId);
    if (element) {
      element.focus();
      setKeyboardState(prev => ({ ...prev, focusedShiftId: shiftId }));
      
      // Announce focus change
      if (accessibilityState.screenReaderEnabled) {
        announceToScreenReader(`Focused shift ${shiftId}`);
      }
    }
  }, [accessibilityState.screenReaderEnabled, announceToScreenReader]);
  
  // Clear keyboard navigation
  const clearKeyboardNavigation = useCallback(() => {
    setKeyboardState(prev => ({
      ...prev,
      focusedShiftId: null,
      isNavigating: false,
      lastKeyPressed: null
    }));
  }, []);

  // Main dimming class calculator with priority hierarchy and accessibility support
  const getDimmingClass = useCallback((
    shiftId: string,
    employeeId?: string | null,
    calendarVariant: CalendarVariant = 'full',
    shiftStatus?: ShiftStatus | string
  ): string => {
    let baseClass = '';
    const isKeyboardFocused = keyboardState.focusedShiftId === shiftId;
    const isHighContrast = accessibilityState.highContrastMode;

    // Priority 0: Dragging (highest priority - dim all shifts during drag, including the dragged one)
    if (draggingShiftId) {
      // All shifts are dimmed during drag, including the original
      baseClass = isHighContrast ? 'opacity-50 brightness-75' : 'opacity-40';
    }
    // Priority 1: Select Open
    else if (shiftWithOpenSelect) {
      if (shiftWithOpenSelect === shiftId) {
        baseClass = isHighContrast ? 'ring-2 ring-blue-600 bg-blue-50' : '';
      } else {
        baseClass = isHighContrast ? 'opacity-60 brightness-75' : 'opacity-30';
      }
    }
    // Priority 2: Hover (only if not insights variant and not keyboard user)
    else if (hoveredShiftId && calendarVariant !== 'insights' && !keyboardState.keyboardUser) {
      if (hoveredShiftId === shiftId) {
        baseClass = isHighContrast ? 'ring-2 ring-yellow-500 bg-yellow-50' : '';
      } else {
        baseClass = isHighContrast ? 'opacity-70 brightness-90' : 'opacity-50';
      }
    }
    // Priority 3: Combined Filters (lowest priority)
    else if (selectedEmployeeFilter || selectedStatusFilter) {
      const shouldDimForEmployee = shouldDimForEmployeeFilter(selectedEmployeeFilter, employeeId);
      const shouldDimForStatus = shouldDimForStatusFilter(selectedStatusFilter, shiftStatus);
      
      // AND logic: dim if EITHER filter says to dim (when both are active, both conditions must be met to NOT dim)
      const shouldDim = shouldDimForEmployee || shouldDimForStatus;
      
      if (shouldDim) {
        baseClass = isHighContrast ? 'opacity-75 brightness-95 grayscale' : 'opacity-40';
      }
    }
    
    // Add keyboard focus ring
    if (isKeyboardFocused && keyboardState.keyboardUser) {
      baseClass += ' ring-2 ring-blue-500 ring-offset-2';
    }
    
    // Add motion-safe transitions
    if (!accessibilityState.reducedMotion) {
      baseClass += ' transition-all duration-200 ease-in-out';
    } else {
      baseClass += ' transition-none';
    }
    
    return baseClass.trim();
  }, [
    draggingShiftId,
    selectedEmployeeFilter,
    selectedStatusFilter,
    hoveredShiftId,
    shiftWithOpenSelect,
    shouldDimForEmployeeFilter,
    shouldDimForStatusFilter,
    keyboardState.focusedShiftId,
    keyboardState.keyboardUser,
    accessibilityState.highContrastMode,
    accessibilityState.reducedMotion
  ]);
  
  // Generate ARIA attributes for accessibility
  const getDimmingAriaAttributes = useCallback((
    shiftId: string,
    employeeId?: string | null,
    calendarVariant: CalendarVariant = 'full',
    shiftStatus?: ShiftStatus | string
  ): DimmingAriaAttributes => {
    const isDimmed = getDimmingClass(shiftId, employeeId, calendarVariant, shiftStatus).includes('opacity');
    const isSelected = shiftWithOpenSelect === shiftId;
    const isHovered = hoveredShiftId === shiftId;
    const isFocused = keyboardState.focusedShiftId === shiftId;
    
    let ariaLabel = `Shift ${shiftId}`;
    if (employeeId) {
      ariaLabel += ` assigned to employee ${employeeId}`;
    } else {
      ariaLabel += ` unassigned`;
    }
    
    if (isDimmed) {
      const filterReasons = [];
      if (selectedEmployeeFilter) {
        filterReasons.push(`employee filter ${selectedEmployeeFilter}`);
      }
      if (selectedStatusFilter) {
        filterReasons.push(`status filter ${selectedStatusFilter}`);
      }
      
      if (filterReasons.length > 0) {
        ariaLabel += `, dimmed due to ${filterReasons.join(' and ')}`;
      } else if (shiftWithOpenSelect && shiftWithOpenSelect !== shiftId) {
        ariaLabel += `, dimmed because another shift dropdown is open`;
      } else if (hoveredShiftId && hoveredShiftId !== shiftId) {
        ariaLabel += `, dimmed because another shift is highlighted`;
      }
    }
    
    if (isSelected) {
      ariaLabel += `, dropdown open`;
    }
    
    if (isHovered && !keyboardState.keyboardUser) {
      ariaLabel += `, highlighted`;
    }
    
    if (isFocused) {
      ariaLabel += `, focused`;
    }
    
    return {
      'aria-hidden': isDimmed && !isFocused ? 'true' : 'false',
      'aria-label': ariaLabel,
      'aria-describedby': `dimming-context-${shiftId}`,
      'role': 'button',
      'tabindex': isDimmed && !isFocused ? '-1' : '0'
    };
  }, [
    getDimmingClass,
    shiftWithOpenSelect,
    hoveredShiftId,
    keyboardState.focusedShiftId,
    keyboardState.keyboardUser,
    selectedEmployeeFilter,
    selectedStatusFilter
  ]);
  
  // List item dimming class calculator for bidirectional dimming in time tracking
  const getListItemDimmingClass = useCallback((
    shiftId: string,
    employeeId?: string | null
  ): string => {
    let baseClass = '';
    const isHighContrast = accessibilityState.highContrastMode;
    
    // Priority 1: Select Open (highest priority - no dimming when select is open)
    if (shiftWithOpenSelect) {
      if (shiftWithOpenSelect === shiftId) {
        baseClass = isHighContrast ? 'ring-2 ring-blue-600' : '';
      } else {
        baseClass = isHighContrast ? 'opacity-60 brightness-75' : 'opacity-30';
      }
    }
    // Priority 2: Hover - bidirectional highlighting and dimming
    else if (hoveredShiftId && !keyboardState.keyboardUser) {
      if (hoveredShiftId === shiftId) {
        // This list item corresponds to the hovered shift - use same hover color as table row hover
        baseClass = isHighContrast ? 'ring-2 ring-yellow-500 bg-yellow-50' : 'bg-muted/50';
      } else {
        // This list item is unrelated to the hovered shift - dim it
        baseClass = isHighContrast ? 'opacity-70 brightness-90' : 'opacity-50';
      }
    }
    // Priority 3: Combined Filters (lowest priority)
    else if (selectedEmployeeFilter || selectedStatusFilter) {
      const shouldDimForEmployee = shouldDimForEmployeeFilter(selectedEmployeeFilter, employeeId);
      const shouldDimForStatus = shouldDimForStatusFilter(selectedStatusFilter, undefined); // List items don't have status directly
      
      // AND logic: dim if EITHER filter says to dim
      const shouldDim = shouldDimForEmployee || shouldDimForStatus;
      
      if (shouldDim) {
        baseClass = isHighContrast ? 'opacity-75 brightness-95 grayscale' : 'opacity-40';
      }
    }
    
    // Add motion-safe transitions
    if (!accessibilityState.reducedMotion) {
      baseClass += ' transition-all duration-200 ease-in-out';
    } else {
      baseClass += ' transition-none';
    }
    
    return baseClass.trim();
  }, [
    selectedEmployeeFilter, 
    selectedStatusFilter,
    hoveredShiftId, 
    shiftWithOpenSelect, 
    shouldDimForEmployeeFilter,
    shouldDimForStatusFilter,
    keyboardState.keyboardUser,
    accessibilityState.highContrastMode,
    accessibilityState.reducedMotion
  ]);

  // Enhanced event handlers with accessibility support
  const eventHandlers = useMemo((): DimmingEventHandlers => {
    const handleMouseEnter = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      if (!keyboardState.keyboardUser && calendarVariant !== 'insights') {
        setPointerState(prev => ({ ...prev, isHovering: true, pointerType: 'mouse' }));
        setHoveredShiftId(shiftId, calendarVariant);
      }
    };
    
    const handleMouseLeave = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      setPointerState(prev => ({ ...prev, isHovering: false }));
      setHoveredShiftId(null, calendarVariant);
    };
    
    const handleFocus = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      setKeyboardState(prev => ({ ...prev, focusedShiftId: shiftId, keyboardUser: true }));
      
      // Register shift element
      const element = document.querySelector(`[data-shift-id="${shiftId}"]`) as HTMLElement;
      if (element) {
        shiftElementsRef.current.set(shiftId, element);
      }
      
      if (accessibilityState.screenReaderEnabled) {
        const ariaAttrs = getDimmingAriaAttributes(shiftId, undefined, calendarVariant);
        announceToScreenReader(ariaAttrs['aria-label']);
      }
    };
    
    const handleBlur = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      setKeyboardState(prev => ({ 
        ...prev, 
        focusedShiftId: prev.focusedShiftId === shiftId ? null : prev.focusedShiftId 
      }));
    };
    
    const handleClick = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      // Handle shift selection/interaction
      if (shiftWithOpenSelect === shiftId) {
        setShiftWithOpenSelect(null);
        if (accessibilityState.screenReaderEnabled) {
          announceToScreenReader('Shift dropdown closed');
        }
      } else {
        setShiftWithOpenSelect(shiftId);
        if (accessibilityState.screenReaderEnabled) {
          announceToScreenReader('Shift dropdown opened');
        }
      }
    };
    
    const handleKeyDown = (event: KeyboardEvent, shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      const { key } = event;
      
      switch (key) {
        case 'Escape':
          event.preventDefault();
          clearAllDimming();
          if (accessibilityState.screenReaderEnabled) {
            announceToScreenReader('All dimming cleared');
          }
          break;
          
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleClick(shiftId, calendarVariant);
          break;
          
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          event.preventDefault();
          const direction = key.replace('Arrow', '').toLowerCase() as FocusDirection;
          const nextShiftId = navigateShifts(direction, shiftId);
          if (nextShiftId) {
            focusShift(nextShiftId);
          }
          break;
        }
          
        case 'Tab':
          // Allow normal tab behavior but update keyboard state
          setKeyboardState(prev => ({ ...prev, keyboardUser: true, isNavigating: true }));
          break;
      }
    };
    
    const handleTouchStart = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      setPointerState(prev => ({ ...prev, pointerType: 'touch', isHovering: true }));
      // On touch, immediately show hover state (no delay)
      if (calendarVariant !== 'insights') {
        setHoveredShiftIdState(shiftId);
      }
    };
    
    const handleTouchEnd = (shiftId: string, calendarVariant: CalendarVariant = 'full') => {
      setPointerState(prev => ({ ...prev, isHovering: false }));
      // Keep hover state briefly for touch feedback
      setTimeout(() => {
        setHoveredShiftIdState(null);
      }, 300);
    };
    
    const handlePointerEnter = (shiftId: string, pointerType: PointerType, calendarVariant: CalendarVariant = 'full') => {
      setPointerState(prev => ({ ...prev, pointerType, isHovering: true }));
      
      if (pointerType === 'touch') {
        handleTouchStart(shiftId, calendarVariant);
      } else {
        handleMouseEnter(shiftId, calendarVariant);
      }
    };
    
    const handlePointerLeave = (shiftId: string, pointerType: PointerType, calendarVariant: CalendarVariant = 'full') => {
      setPointerState(prev => ({ ...prev, isHovering: false }));
      
      if (pointerType === 'touch') {
        handleTouchEnd(shiftId, calendarVariant);
      } else {
        handleMouseLeave(shiftId, calendarVariant);
      }
    };
    
    return {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave
    };
  }, [
    keyboardState.keyboardUser,
    accessibilityState.screenReaderEnabled,
    shiftWithOpenSelect,
    setHoveredShiftId,
    getDimmingAriaAttributes,
    announceToScreenReader,
    navigateShifts,
    focusShift
  ]);
  
  // Enhanced tooltip management with coordination
  const setActiveTooltipShiftId = useCallback((shiftId: string | null, preferredSide: 'top' | 'bottom' | 'left' | 'right' = 'top') => {
    // ALWAYS allow clearing tooltip (when shiftId is null)
    // This prevents stuck tooltip states
    if (shiftId === null) {
      setActiveTooltipShiftIdState(null);
      return;
    }

    // Only block SETTING new tooltip when select is open
    if (shiftWithOpenSelect) {
      return;
    }

    setActiveTooltipShiftIdState(shiftId);
    setTooltipPreferredSide(preferredSide);
  }, [shiftWithOpenSelect]);
  
  // Auto-hide tooltip when select opens
  useEffect(() => {
    if (shiftWithOpenSelect && activeTooltipShiftId) {
      setActiveTooltipShiftIdState(null);
    }
  }, [shiftWithOpenSelect, activeTooltipShiftId]);
  
  // Tooltip coordination logic
  const shouldShowTooltip = useCallback((shiftId: string) => {
    // Don't show tooltip if any select is open
    if (shiftWithOpenSelect) {
      return false;
    }
    
    // Don't show tooltip if keyboard user is navigating
    if (keyboardState.keyboardUser && keyboardState.isNavigating) {
      return false;
    }
    
    // Show tooltip if this is the active tooltip shift
    return activeTooltipShiftId === shiftId;
  }, [shiftWithOpenSelect, keyboardState.keyboardUser, keyboardState.isNavigating, activeTooltipShiftId]);
  
  // Smart tooltip positioning
  const getTooltipSide = useCallback((shiftId: string, defaultSide: 'top' | 'bottom' | 'left' | 'right' = 'top') => {
    // If there's a preferred side from the context, use it
    if (activeTooltipShiftId === shiftId) {
      return tooltipPreferredSide;
    }
    
    // Otherwise use the provided default
    return defaultSide;
  }, [activeTooltipShiftId, tooltipPreferredSide]);
  
  // Clear all dimming states with accessibility announcements
  const clearAllDimming = useCallback(() => {
    setSelectedEmployeeFilter(null);
    setSelectedStatusFilter(null);
    setHoveredShiftIdState(null);
    setShiftWithOpenSelect(null);
    setActiveTooltipShiftIdState(null);
    clearKeyboardNavigation();

    // Clear any pending timeouts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (keyboardTimeoutRef.current) {
      clearTimeout(keyboardTimeoutRef.current);
      keyboardTimeoutRef.current = null;
    }

    // Announce to screen reader
    if (accessibilityState.screenReaderEnabled) {
      announceToScreenReader('All dimming and filters cleared');
    }
  }, [clearKeyboardNavigation, accessibilityState.screenReaderEnabled, announceToScreenReader]);

  // STATE COORDINATION: Clear hover and tooltip when dragging starts
  useEffect(() => {
    if (draggingShiftId) {
      // Clear hover state immediately when drag starts
      setHoveredShiftIdState(null);
      setActiveTooltipShiftIdState(null);

      // Clear any pending hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  }, [draggingShiftId]);

  // STATE COORDINATION: Clear hover and tooltip when select opens
  useEffect(() => {
    if (shiftWithOpenSelect) {
      // Clear hover state when select opens (keep only the selected shift visible)
      setHoveredShiftIdState(null);

      // Clear any pending hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  }, [shiftWithOpenSelect]);

  // Note: Removed blur/visibilitychange listeners that were clearing hover/tooltip state
  // These caused cascading re-renders and scroll position reset when switching tabs
  // The hover/tooltip state naturally clears when user stops hovering - no aggressive cleanup needed

  // Global keyboard event handling for calendar area
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Only handle if we have a calendar container in focus tree
      if (!containerRef.current || !document.activeElement) return;
      
      const isInCalendar = containerRef.current.contains(document.activeElement) ||
                          document.activeElement.closest('[data-calendar-container]');
      
      if (!isInCalendar) return;
      
      // Handle global escape to clear all dimming
      if (event.key === 'Escape') {
        event.preventDefault();
        clearAllDimming();
      }
    };
    
    const handleGlobalMouseLeave = (event: MouseEvent) => {
      // Clear dimming when mouse leaves entire calendar area
      const target = event.relatedTarget as Element;
      if (!target || (!containerRef.current?.contains(target) && 
                     !target.closest('[data-calendar-container]'))) {
        setHoveredShiftId(null);
      }
    };
    
    document.addEventListener('keydown', handleGlobalKeyDown);
    
    // Add mouse leave detection to calendar container
    const calendarContainers = document.querySelectorAll('[data-calendar-container]');
    calendarContainers.forEach(container => {
      container.addEventListener('mouseleave', handleGlobalMouseLeave as EventListener);
    });
    
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      calendarContainers.forEach(container => {
        container.removeEventListener('mouseleave', handleGlobalMouseLeave as EventListener);
      });
    };
  }, [clearAllDimming, setHoveredShiftId]);
  
  // Cleanup timeouts and refs on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (keyboardTimeoutRef.current) {
        clearTimeout(keyboardTimeoutRef.current);
      }
      shiftElementsRef.current.clear();
    };
  }, []);

  // Memoize context value to prevent unnecessary rerenders
  // Only recreate when actual state or callbacks change
  const value: DimmingContextType = useMemo(() => ({
    // Core state
    selectedEmployeeFilter,
    selectedStatusFilter,
    hoveredShiftId,
    shiftWithOpenSelect,
    activeTooltipShiftId,
    draggingShiftId,

    // Accessibility and interaction state
    accessibilityState,
    keyboardState,
    pointerState,

    // State setters
    setSelectedEmployeeFilter,
    setSelectedStatusFilter,
    setHoveredShiftId,
    setShiftWithOpenSelect,
    setActiveTooltipShiftId,
    setDraggingShiftId,

    // Event handlers
    eventHandlers,

    // Helper functions
    getDimmingClass,
    getDimmingAriaAttributes,
    getListItemDimmingClass,
    shouldDimForEmployeeFilter,
    shouldDimForStatusFilter,

    // Tooltip coordination functions
    shouldShowTooltip,
    getTooltipSide,

    // Navigation functions
    navigateShifts,
    focusShift,

    // Accessibility functions
    announceToScreenReader,
    updateAccessibilityState,

    // Clear functions
    clearAllDimming,
    clearKeyboardNavigation,
  }), [
    // Core state dependencies
    selectedEmployeeFilter,
    selectedStatusFilter,
    hoveredShiftId,
    shiftWithOpenSelect,
    activeTooltipShiftId,
    draggingShiftId,
    accessibilityState,
    keyboardState,
    pointerState,
    // All callbacks are already memoized with useCallback, so they're stable
    setSelectedEmployeeFilter,
    setSelectedStatusFilter,
    setHoveredShiftId,
    setShiftWithOpenSelect,
    setActiveTooltipShiftId,
    setDraggingShiftId,
    eventHandlers,
    getDimmingClass,
    getDimmingAriaAttributes,
    getListItemDimmingClass,
    shouldDimForEmployeeFilter,
    shouldDimForStatusFilter,
    shouldShowTooltip,
    getTooltipSide,
    navigateShifts,
    focusShift,
    announceToScreenReader,
    updateAccessibilityState,
    clearAllDimming,
    clearKeyboardNavigation,
  ]);

  return (
    <DimmingContext.Provider value={value}>
      <div
        ref={containerRef}
        data-calendar-container
        style={{ position: 'relative' }}
      >
        {children}
      </div>
    </DimmingContext.Provider>
  );
};

export const useDimmingContext = () => {
  const context = useContext(DimmingContext);
  if (context === undefined) {
    throw new Error('useDimmingContext must be used within a DimmingProvider');
  }
  return context;
};

// Export context for direct access if needed
export { DimmingContext };